import { useContext } from "react";
import { Badge } from "@/components/ui/badge";
import { DebugContext } from "@/routes/_authenticated/ava/$chatId";

interface ResultRendererProps {
  result: any;
  status: string;
  fallbackMessage?: string;
}

// Utility to detect if a string is JSON
const isJsonString = (str: string): boolean => {
  try {
    const parsed = JSON.parse(str);
    return typeof parsed === "object" && parsed !== null;
  } catch {
    return false;
  }
};

// Utility to safely parse JSON
const safeJsonParse = (str: string): any => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

// Component to render JSON in debug mode with proper styling
const JsonDebugRenderer = ({ data }: { data: any }) => (
  <div className="mt-2 rounded bg-gray-50 p-2">
    <div className="mb-1 text-xs font-medium text-gray-600">
      Debug - Raw Result:
    </div>
    <div className="max-h-32 overflow-auto rounded bg-white p-2 font-mono text-xs">
      <pre className="whitespace-pre-wrap break-all text-gray-700">
        {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  </div>
);

// Main component for rendering tool results
export function ResultRenderer({
  result,
  status,
  fallbackMessage = "Tool completed successfully",
}: ResultRendererProps) {
  const { isDebugMode } = useContext(DebugContext);

  // Don't render if no result or still running
  if (!result || status === "running") {
    return null;
  }

  // Handle string results
  if (typeof result === "string") {
    // Check if it's JSON
    if (isJsonString(result)) {
      const parsedJson = safeJsonParse(result);

      return (
        <div className="rounded bg-white p-2 text-xs">
          {/* In non-debug mode, show user-friendly message */}
          {!isDebugMode && (
            <div className="text-gray-700">
              <Badge variant="outline" className="mr-2 text-xs">
                ✓ Complete
              </Badge>
              {fallbackMessage}
            </div>
          )}

          {/* In debug mode, show formatted JSON */}
          {isDebugMode && <JsonDebugRenderer data={parsedJson || result} />}
        </div>
      );
    } else {
      // Regular string - safe to display
      return (
        <div className="rounded bg-white p-2 text-xs text-gray-700">
          <div className="max-h-24 overflow-auto break-words">{result}</div>
          {isDebugMode && result.length > 200 && (
            <div className="mt-1 text-xs text-gray-500">
              (Text truncated in UI, full content: {result.length} chars)
            </div>
          )}
        </div>
      );
    }
  }

  // Handle object results
  if (typeof result === "object") {
    return (
      <div className="rounded bg-white p-2 text-xs">
        {/* In non-debug mode, show user-friendly message */}
        {!isDebugMode && (
          <div className="text-gray-700">
            <Badge variant="outline" className="mr-2 text-xs">
              ✓ Complete
            </Badge>
            {fallbackMessage}
          </div>
        )}

        {/* In debug mode, show formatted object */}
        {isDebugMode && <JsonDebugRenderer data={result} />}
      </div>
    );
  }

  // Fallback for other types
  return (
    <div className="rounded bg-white p-2 text-xs text-gray-700">
      <Badge variant="outline" className="mr-2 text-xs">
        ✓ Complete
      </Badge>
      {fallbackMessage}
    </div>
  );
}
